import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import parentServicesService from '../../services/parentServicesService';
import examOfficeService from '../../services/examOfficeService';
import DialogModal from '../../components/DialogModal';
import { CalendarBlank, FileText, ClipboardText, Warning, ChartBar, LockKey } from '@phosphor-icons/react';

const TABS = [
  ['absences', 'Absence Requests', CalendarBlank],
  ['documents', 'Document Requests', FileText],
  ['consents', 'Permission Slips', ClipboardText],
  ['complaints', 'Complaints', Warning],
  ['exam-results', 'Exam Results', ChartBar],
  ['kiosk-pin', 'Kiosk PIN', LockKey],
];

const DOCUMENT_TYPES = [
  ['transfer_certificate', 'Transfer Certificate'],
  ['leaving_certificate', 'Leaving Certificate'],
  ['transcript', 'Transcript'],
  ['bonafide_letter', 'Bonafide Letter'],
  ['id_card_replacement', 'ID Card Replacement'],
  ['other', 'Other'],
];

const COMPLAINT_CATEGORIES = ['academic', 'bullying', 'facility', 'staff_conduct', 'billing', 'transport', 'other'];

function ParentServicesSection({ kids }) {
  const [tab, setTab] = useState('absences');
  const [absences, setAbsences] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [consents, setConsents] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [myRemarks, setMyRemarks] = useState([]);
  const [examChildId, setExamChildId] = useState('');
  const [examMarks, setExamMarks] = useState([]);
  const [examMarksLoading, setExamMarksLoading] = useState(false);

  const [absenceForm, setAbsenceForm] = useState({ student_id: '', request_type: 'absence', start_date: '', end_date: '', expected_arrival_time: '', reason: '' });
  const [documentForm, setDocumentForm] = useState({ student_id: '', document_type: 'transcript', reason: '' });
  const [complaintForm, setComplaintForm] = useState({ student_id: '', category: 'other', severity: 'medium', subject: '', description: '' });
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }
  const [kioskPinForm, setKioskPinForm] = useState({ student_id: '', pin: '', confirmPin: '' });
  const [savingKioskPin, setSavingKioskPin] = useState(false);

  const load = useCallback(async () => {
    try {
      const [absenceData, documentData, consentData, complaintData, remarkData] = await Promise.all([
        parentServicesService.listAbsenceRequests(),
        parentServicesService.listDocumentRequests(),
        parentServicesService.listMyConsentResponses(),
        parentServicesService.listComplaints(),
        examOfficeService.listRemarkRequests(),
      ]);
      setAbsences(absenceData || []);
      setDocuments(documentData || []);
      setConsents(consentData || []);
      setComplaints(complaintData || []);
      setMyRemarks(remarkData || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to load requests');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!examChildId && kids.length) setExamChildId(kids[0].id);
  }, [kids, examChildId]);

  const loadExamMarks = useCallback(async () => {
    if (!examChildId) return;
    setExamMarksLoading(true);
    try {
      const data = await examOfficeService.listStudentMarks(examChildId);
      setExamMarks(data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to load exam results');
    } finally {
      setExamMarksLoading(false);
    }
  }, [examChildId]);

  useEffect(() => { loadExamMarks(); }, [loadExamMarks]);

  const requestRemark = (mark) => {
    setPromptDialog({
      title: 'Request Remark',
      message: 'Why do you want this result rechecked?',
      defaultValue: '',
      onSubmit: async (reason) => {
        if (!reason || !reason.trim()) return;
        try {
          await examOfficeService.createRemarkRequest({ student_id: examChildId, exam_component_id: mark.exam_component_id, reason: reason.trim() });
          toast.success('Remark request submitted');
          load();
        } catch (error) {
          toast.error(error.response?.data?.detail || error.message || 'Unable to submit remark request');
        }
      },
    });
  };

  const cancelRemark = async (id) => {
    try { await examOfficeService.cancelRemarkRequest(id); toast.success('Request cancelled'); load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Unable to cancel'); }
  };

  const activeRemarkFor = (componentId) => myRemarks.find((r) => r.exam_component_id === componentId && ['pending', 'under_review'].includes(r.status));

  const childSelect = (value, onChange) => (
    <select className="form-control" value={value} onChange={onChange} required>
      <option value="">Select child</option>
      {kids.map((c) => <option key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</option>)}
    </select>
  );

  const submitAbsence = async (e) => {
    e.preventDefault();
    const isLate = absenceForm.request_type === 'late_arrival';
    try {
      await parentServicesService.createAbsenceRequest({
        student_id: absenceForm.student_id,
        request_type: absenceForm.request_type,
        start_date: absenceForm.start_date,
        end_date: isLate ? absenceForm.start_date : absenceForm.end_date,
        expected_arrival_time: isLate ? absenceForm.expected_arrival_time : undefined,
        reason: absenceForm.reason,
      });
      setAbsenceForm({ student_id: '', request_type: 'absence', start_date: '', end_date: '', expected_arrival_time: '', reason: '' });
      toast.success(isLate ? 'Late arrival notice submitted' : 'Absence request submitted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to submit request');
    }
  };

  const submitKioskPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(kioskPinForm.pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    if (kioskPinForm.pin !== kioskPinForm.confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setSavingKioskPin(true);
    try {
      await parentServicesService.setKioskPin(kioskPinForm.student_id, kioskPinForm.pin);
      toast.success('Kiosk PIN saved');
      setKioskPinForm({ student_id: kioskPinForm.student_id, pin: '', confirmPin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to save PIN');
    } finally {
      setSavingKioskPin(false);
    }
  };

  const cancelAbsence = async (id) => {
    try { await parentServicesService.cancelAbsenceRequest(id); toast.success('Request cancelled'); load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Unable to cancel'); }
  };

  const submitDocument = async (e) => {
    e.preventDefault();
    try {
      await parentServicesService.createDocumentRequest(documentForm);
      setDocumentForm({ student_id: '', document_type: 'transcript', reason: '' });
      toast.success('Document request submitted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to submit request');
    }
  };

  const cancelDocument = async (id) => {
    try { await parentServicesService.cancelDocumentRequest(id); toast.success('Request cancelled'); load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Unable to cancel'); }
  };

  const downloadDocumentFile = async (doc) => {
    try {
      if (doc.fulfillment_file_url?.includes('/transcript.pdf')) {
        const response = await parentServicesService.downloadTranscript(doc.id);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript_${doc.student_id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        window.open(doc.fulfillment_file_url, '_blank', 'noreferrer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to download file');
    }
  };

  const respondConsent = async (response, status) => {
    if (status === 'declined') {
      setPromptDialog({
        title: 'Decline Permission Slip',
        message: 'Reason for declining (optional):',
        defaultValue: '',
        onSubmit: async (notesInput) => {
          const notes = notesInput || null;
          try {
            await parentServicesService.submitConsentResponse(response.id, { status, notes });
            toast.success('Consent declined');
            load();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Unable to submit response');
          }
        },
      });
      return;
    }
    try {
      await parentServicesService.submitConsentResponse(response.id, { status, notes: null });
      toast.success('Consent granted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to submit response');
    }
  };

  const submitComplaint = async (e) => {
    e.preventDefault();
    try {
      await parentServicesService.createComplaint({ ...complaintForm, student_id: complaintForm.student_id || null });
      setComplaintForm({ student_id: '', category: 'other', severity: 'medium', subject: '', description: '' });
      toast.success('Complaint submitted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to submit complaint');
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="tabs-container">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} className={`tab-btn ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            <Icon size={18} /> {label}
          </button>
        ))}
      </div>

      {tab === 'absences' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Notify the school of an absence or late arrival</h3>
            <form onSubmit={submitAbsence} style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end' }}>
              {childSelect(absenceForm.student_id, (e) => setAbsenceForm({ ...absenceForm, student_id: e.target.value }))}
              <select className="form-control" value={absenceForm.request_type} onChange={(e) => setAbsenceForm({ ...absenceForm, request_type: e.target.value })}>
                <option value="absence">Absence</option>
                <option value="late_arrival">Running Late</option>
              </select>
              <input
                className="form-control" type="date" required
                value={absenceForm.start_date}
                onChange={(e) => setAbsenceForm({ ...absenceForm, start_date: e.target.value })}
              />
              {absenceForm.request_type === 'absence' ? (
                <input className="form-control" type="date" required value={absenceForm.end_date} onChange={(e) => setAbsenceForm({ ...absenceForm, end_date: e.target.value })} />
              ) : (
                <input
                  className="form-control" type="time" required
                  value={absenceForm.expected_arrival_time}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, expected_arrival_time: e.target.value })}
                  title="Expected arrival time"
                />
              )}
              <input className="form-control" placeholder="Reason" required value={absenceForm.reason} onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })} />
              <button className="btn btn-primary" type="submit">Submit</button>
            </form>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>My requests</h3>
            <table className="table">
              <thead><tr><th>Type</th><th>When</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {absences.map((a) => (
                  <tr key={a.id}>
                    <td className="text-capitalize">{a.request_type === 'late_arrival' ? 'Running Late' : 'Absence'}</td>
                    <td>{a.request_type === 'late_arrival' ? `${a.start_date}, ~${a.expected_arrival_time}` : `${a.start_date} to ${a.end_date}`}</td>
                    <td>{a.reason}</td>
                    <td className="text-capitalize">{a.status}{a.review_notes ? ` — ${a.review_notes}` : ''}</td>
                    <td>{a.status === 'pending' && <button className="btn btn-sm btn-outline-danger" onClick={() => cancelAbsence(a.id)}>Cancel</button>}</td>
                  </tr>
                ))}
                {!absences.length && <tr><td colSpan={5} className="text-muted">No absence or late-arrival requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'documents' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Request a document</h3>
            <form onSubmit={submitDocument} style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'end' }}>
              {childSelect(documentForm.student_id, (e) => setDocumentForm({ ...documentForm, student_id: e.target.value }))}
              <select className="form-control" value={documentForm.document_type} onChange={(e) => setDocumentForm({ ...documentForm, document_type: e.target.value })}>
                {DOCUMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input className="form-control" placeholder="Reason (optional)" value={documentForm.reason} onChange={(e) => setDocumentForm({ ...documentForm, reason: e.target.value })} />
              <button className="btn btn-primary" type="submit">Submit</button>
            </form>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>My requests</h3>
            <table className="table">
              <thead><tr><th>Document</th><th>Status</th><th>File</th><th>Actions</th></tr></thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.id}>
                    <td className="text-capitalize">{d.document_type.replace(/_/g, ' ')}</td>
                    <td className="text-capitalize">{d.status.replace('_', ' ')}</td>
                    <td>{d.fulfillment_file_url ? <button className="btn btn-sm btn-outline-primary" onClick={() => downloadDocumentFile(d)}>Download</button> : '-'}</td>
                    <td>{d.status === 'pending' && <button className="btn btn-sm btn-outline-danger" onClick={() => cancelDocument(d.id)}>Cancel</button>}</td>
                  </tr>
                ))}
                {!documents.length && <tr><td colSpan={4} className="text-muted">No document requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'consents' && (
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Permission slips awaiting a response</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {consents.map((c) => (
              <div key={c.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 700 }}>{c.form_title} — {c.student_name}</div>
                <div style={{ color: '#6b7280', fontSize: 13, margin: '4px 0' }}>{c.form_description}</div>
                {c.respond_by && <div style={{ fontSize: 12, color: '#6b7280' }}>Respond by {c.respond_by}</div>}
                {c.status === 'pending' ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-sm btn-success" onClick={() => respondConsent(c, 'granted')}>Grant</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => respondConsent(c, 'declined')}>Decline</button>
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontWeight: 700, textTransform: 'capitalize', color: c.status === 'granted' ? '#16a34a' : '#dc2626' }}>{c.status}</div>
                )}
              </div>
            ))}
            {!consents.length && <div className="text-muted">No permission slips right now.</div>}
          </div>
        </div>
      )}

      {tab === 'complaints' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Raise a complaint</h3>
            <form onSubmit={submitComplaint} style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                <select className="form-control" value={complaintForm.student_id} onChange={(e) => setComplaintForm({ ...complaintForm, student_id: e.target.value })}>
                  <option value="">Not about a specific child</option>
                  {kids.map((c) => <option key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</option>)}
                </select>
                <select className="form-control" value={complaintForm.category} onChange={(e) => setComplaintForm({ ...complaintForm, category: e.target.value })}>
                  {COMPLAINT_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <select className="form-control" value={complaintForm.severity} onChange={(e) => setComplaintForm({ ...complaintForm, severity: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <input className="form-control" placeholder="Subject" required value={complaintForm.subject} onChange={(e) => setComplaintForm({ ...complaintForm, subject: e.target.value })} />
              <textarea className="form-control" placeholder="Describe the issue" required value={complaintForm.description} onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })} />
              <button className="btn btn-primary" type="submit" style={{ justifySelf: 'start' }}>Submit</button>
            </form>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>My complaints</h3>
            <table className="table">
              <thead><tr><th>Subject</th><th>Category</th><th>Status</th></tr></thead>
              <tbody>
                {complaints.map((c) => (
                  <tr key={c.id}>
                    <td>{c.subject}</td>
                    <td className="text-capitalize">{c.category.replace('_', ' ')}</td>
                    <td className="text-capitalize">{c.status.replace('_', ' ')}</td>
                  </tr>
                ))}
                {!complaints.length && <tr><td colSpan={3} className="text-muted">No complaints filed.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'exam-results' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Published exam results</h3>
            <div style={{ marginBottom: 12 }}>
              <select className="form-control" value={examChildId} onChange={(e) => setExamChildId(e.target.value)}>
                {kids.map((c) => <option key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</option>)}
              </select>
            </div>
            {examMarksLoading ? (
              <div className="text-muted">Loading...</div>
            ) : (
              <table className="table">
                <thead><tr><th>Exam</th><th>Component</th><th>Score</th><th>Actions</th></tr></thead>
                <tbody>
                  {examMarks.map((m) => {
                    const active = activeRemarkFor(m.exam_component_id);
                    return (
                      <tr key={m.id}>
                        <td>{m.exam_session_name} — {m.exam_date}</td>
                        <td>{m.component_name}{m.annulled ? ' (annulled)' : ''}</td>
                        <td>{m.score} / {m.max_marks}</td>
                        <td>
                          {active ? (
                            <span className="text-capitalize">{active.status.replace('_', ' ')}</span>
                          ) : (
                            <button className="btn btn-sm btn-outline-primary" onClick={() => requestRemark(m)}>Request Remark</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!examMarks.length && <tr><td colSpan={4} className="text-muted">No published results yet.</td></tr>}
                </tbody>
              </table>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>My remark requests</h3>
            <table className="table">
              <thead><tr><th>Component</th><th>Reason</th><th>Original</th><th>Revised</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {myRemarks.map((r) => (
                  <tr key={r.id}>
                    <td>{r.component_name}</td>
                    <td>{r.reason}</td>
                    <td>{r.original_score}</td>
                    <td>{r.revised_score ?? '—'}</td>
                    <td className="text-capitalize">{r.status.replace('_', ' ')}{r.review_notes ? ` — ${r.review_notes}` : ''}</td>
                    <td>{r.status === 'pending' && <button className="btn btn-sm btn-outline-danger" onClick={() => cancelRemark(r.id)}>Cancel</button>}</td>
                  </tr>
                ))}
                {!myRemarks.length && <tr><td colSpan={6} className="text-muted">No remark requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'kiosk-pin' && (
        <div className="card" style={{ padding: 16, maxWidth: 420 }}>
          <h3 style={{ marginTop: 0 }}>Set up canteen kiosk login</h3>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: -4 }}>
            Schools with a shared tablet let a child without their own phone scan their ID card to
            order and pay for food at the canteen themselves. Set a 4-digit PIN here so your child
            can use it — they scan their card at the kiosk, then enter this PIN.
          </p>
          <form onSubmit={submitKioskPin} style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {childSelect(kioskPinForm.student_id, (e) => setKioskPinForm({ ...kioskPinForm, student_id: e.target.value }))}
            <input
              className="form-control" type="password" inputMode="numeric" maxLength={4}
              placeholder="4-digit PIN" required
              value={kioskPinForm.pin}
              onChange={(e) => setKioskPinForm({ ...kioskPinForm, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            />
            <input
              className="form-control" type="password" inputMode="numeric" maxLength={4}
              placeholder="Confirm PIN" required
              value={kioskPinForm.confirmPin}
              onChange={(e) => setKioskPinForm({ ...kioskPinForm, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            />
            <button className="btn btn-primary" type="submit" disabled={savingKioskPin || !kioskPinForm.student_id} style={{ justifySelf: 'start' }}>
              {savingKioskPin ? 'Saving...' : 'Save PIN'}
            </button>
          </form>
        </div>
      )}

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default ParentServicesSection;
